import React, { useState } from 'react';
import Car from '../icon/Car';
import Smoke from '../icon/Smoke';
import Cow from '../icon/Cow';
import Shower from '../icon/Shower';
import Toxicity from '../icon/Toxicity';
import DetailedProduct from '../model/DetailedProductModel'; // Assurez-vous que le chemin est correct

interface ProductImpactProps {
  product: DetailedProduct;
}

const ProductImpact: React.FC<ProductImpactProps> = ({ product }) => {
  const [isCarExpanded, setIsCarExpanded] = useState(false);
  const [isCowExpanded, setIsCowExpanded] = useState(false);
  const [isShowerExpanded, setIsShowerExpanded] = useState(false);
  const [isCancerExpanded, setIsCancerExpanded] = useState(false);

  // Calcul des valeurs
  const carKilometer = product.impact_global_cch / 0.130;
  const cowFart = product.impact_global_cch / 7.67;
  const shower = product.impact_global_wtu / 0.0475;
  const cancer = product.impact_global_htc * 1000000000;

  const toggleCarExpand = () => setIsCarExpanded(!isCarExpanded);
  const toggleCowExpand = () => setIsCowExpanded(!isCowExpanded);
  const toggleShowerExpand = () => setIsShowerExpanded(!isShowerExpanded);
  const toggleCancerExpand = () => setIsCancerExpanded(!isCancerExpanded);

  const launchURL = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <div className="flex flex-col">
      {/* Section pour la voiture */}
      <div className="h-2.5" />
      <div onClick={toggleCarExpand} className="flex justify-between items-center cursor-pointer">
        <div className="flex items-center">
          <div className="pl-4">
            <Car />
          </div>
          <Smoke />
          <div className="ml-3">
            <div className="text-sm font-medium">Équivalent d'une voiture</div>
          </div>
        </div>
        <div className="flex items-center">
          <div className="text-xs">{carKilometer.toFixed(0)} km</div>
          <div className={`ml-2 transform transition-transform duration-200 ${isCarExpanded ? 'rotate-180' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      {isCarExpanded && (
        <div className="px-4 py-1.25">
          <div className="text-xs">
            La production de ce vêtement a émis l'équivalent en CO² d'une voiture diesel parcourant {carKilometer.toFixed(0)} km*
          </div>
          <div className="text-xs text-gray-500">
            *d'après{' '}
            <span
              onClick={() => launchURL('https://carlabelling.ademe.fr/chiffrescles/r/evolutionTauxCo2')}
              className="underline cursor-pointer"
            >
              l'Ademe
            </span>
          </div>
        </div>
      )}
      <div className="h-2.5" />
      <hr className="border-[rgb(161_153_153)]" />
      <div className="h-2.5" />

      {/* Section pour les vaches */}
      <div onClick={toggleCowExpand} className="flex justify-between items-center cursor-pointer">
        <div className="flex items-center">
          <div className="pl-4">
            <Cow />
          </div>
          <Smoke />
          <div className="ml-3">
            <div className="text-sm font-medium">Équivalent d'une vache</div>
          </div>
        </div>
        <div className="flex items-center">
          <div className="text-xs">{cowFart.toFixed(2)} /jours</div>
          <div className={`ml-2 transform transition-transform duration-200 ${isCowExpanded ? 'rotate-180' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      {isCowExpanded && (
        <div className="px-4 py-1.25">
          <div className="text-xs">
            La production de ce vêtement a émis l'équivalent en CO² de {cowFart.toFixed(2)} vaches en une journée*
          </div>
          <div className="text-xs text-gray-500">
            *d'après{' '}
            <span
              onClick={() => launchURL('https://www.sillonbelge.be/8471/article/2022-01-08/vaches-prairies-methane-et-co2-le-bilan')}
              className="underline cursor-pointer"
            >
              le sillon Belge
            </span>
          </div>
        </div>
      )}
      <div className="h-2.5" />
      <hr className="border-[rgb(161_153_153)]" />
      <div className="h-1.5" />

      {/* Section pour les douches */}
      <div onClick={toggleShowerExpand} className="flex justify-between items-center cursor-pointer">
        <div className="flex items-center">
          <div className="pl-4">
            <Shower />
          </div>
          <div className="ml-6">
            <div className="text-sm font-medium">Consommation en eau</div>
          </div>
        </div>
        <div className="flex items-center">
          <div className="text-xs">{shower.toFixed(0)} douches</div>
          <div className={`ml-2 transform transition-transform duration-200 ${isShowerExpanded ? 'rotate-180' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      {isShowerExpanded && (
        <div className="px-4 py-1.25">
          <div className="text-xs">
            La production de ce vêtement a consommé l'équivalent de {shower.toFixed(0)} douches*
          </div>
          <div className="text-xs text-gray-500">
            *d'après{' '}
            <span
              onClick={() => launchURL('https://agirpourlatransition.ademe.fr/particuliers/conso/conso-responsable/astuces-economiser-leau-a-maison-alleger-factures')}
              className="underline cursor-pointer"
            >
              l'Ademe
            </span>
          </div>
        </div>
      )}
      <div className="h-1.5" />
      <hr className="border-[rgb(161_153_153)]" />
      <div className="h-2.5" />

      {/* Section pour la toxicité */}
      <div onClick={toggleCancerExpand} className="flex justify-between items-center cursor-pointer">
        <div className="flex items-center">
          <div className="pl-4">
            <Toxicity />
          </div>
          <div className="ml-7">
            <div className="text-sm font-medium">Toxicité - Cancer</div>
          </div>
        </div>
        <div className="flex items-center">
          <div className="text-xs">{cancer.toFixed(2)} sur 1 milliard</div>
          <div className={`ml-2 transform transition-transform duration-200 ${isCancerExpanded ? 'rotate-180' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      {isCancerExpanded && (
        <div className="px-4 py-1.25">
          <div className="text-xs">
            Le port de ce vêtement cause {cancer.toFixed(2)} cas de cancer par an sur 1 milliard de personnes*
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductImpact;
